import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Group, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import Table from 'components/Table';
import useAssignGate from 'hooks/workout/useAssignGate';
import useAssignWorkout from 'hooks/workout/useAssignWorkout';
import useWorkoutTemplate from 'hooks/workoutTemplate/useWorkoutTemplate';
import useWorkoutTemplates from 'hooks/workoutTemplate/useWorkoutTemplates';
import { useNavigate } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import type { Workout, WorkoutTemplateList } from 'types';
import notificationHandler from 'utils/notifications';
import type { AssignmentTarget } from 'utils/workout.helpers';

import WorkoutPreviewModal from '../../WorkoutPreviewModal';
import useAssignTracking from '../useAssignTracking';
import {
  buildPreviewDataFromTemplate,
  buildWorkoutWriteFromTemplate,
  mapTemplateToFormValues,
} from '../utils/templateToWorkoutWrite';

interface PanelTemplateProps {
  target: AssignmentTarget;
  date: string;
  onWorkoutAssigned?: (workout: Workout) => void;
  onClose: () => void;
  view?: 'week' | 'agenda';
  replacesWorkoutId?: string;
  defaultVisibleToAthletes: boolean;
}

interface TemplatesEmptyStateProps {
  search: string;
  canCreate: boolean;
  onCreateNew: () => void;
}

const TemplatesEmptyState = ({ search, canCreate, onCreateNew }: TemplatesEmptyStateProps): JSX.Element => {
  const query = search.trim();

  if (query) {
    return (
      <Text size='sm' color='dimmed' data-cy='templatesNoResults'>
        No templates match “{query}”.
      </Text>
    );
  }

  return (
    <Stack align='center' spacing='xs' data-cy='templatesEmpty'>
      <Text size='sm' color='dimmed'>
        No templates yet
      </Text>
      <Button
        size='xs'
        variant='light'
        leftIcon={<IconPlus size={14} />}
        onClick={onCreateNew}
        disabled={!canCreate}
      >
        Create New
      </Button>
    </Stack>
  );
};

const TABLE_HEIGHT = '25rem';

const PanelTemplate = ({
  target,
  date,
  onWorkoutAssigned,
  onClose,
  view,
  replacesWorkoutId,
  defaultVisibleToAthletes,
}: PanelTemplateProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { canMutate } = useAssignGate();
  const [search, setSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkoutTemplateList | null>(null);
  const [previewOpened, setPreviewOpened] = useState(false);

  const { successNotification, errorNotification } = notificationHandler();
  const assignMutation = useAssignWorkout(replacesWorkoutId);
  const { trackConfirmed } = useAssignTracking(target);

  const {
    data: templatesData,
    isLoading,
    isSuccess,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWorkoutTemplates({
    search: search || undefined,
    limit: '20',
  });

  const { data: templateResponse, isLoading: templateLoading } = useWorkoutTemplate(
    previewOpened && selectedTemplate ? selectedTemplate.workoutTemplateId : undefined,
  );
  const templateData = templateResponse?.data;

  const tableData = useMemo(() => {
    if (!isSuccess || !templatesData?.pages) return [];
    return templatesData.pages.flatMap(page => page.data.results);
  }, [isSuccess, templatesData]);

  const columns: Column<WorkoutTemplateList>[] = useMemo(
    () => [
      {
        Header: 'Workout Template Name',
        accessor: 'name' as const,
        Cell: ({ row }: CellProps<WorkoutTemplateList>) => (
          <Group spacing='xs' noWrap>
            <Text weight='bold'>{row.original.name}</Text>
          </Group>
        ),
      },
    ],
    [],
  );

  const handleRowClick = useCallback((row: WorkoutTemplateList) => {
    setSelectedTemplate(row);
    setPreviewOpened(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpened(false);
    setSelectedTemplate(null);
  }, []);

  const handleAssign = useCallback(
    (isVisibleToAthletes: boolean): void => {
      if (!selectedTemplate || !templateData) return;
      const payload = buildWorkoutWriteFromTemplate(templateData, date, target, isVisibleToAthletes);
      assignMutation.mutate(payload, {
        onSuccess: res => {
          trackConfirmed(payload);
          successNotification(
            'Workout assigned',
            `"${selectedTemplate.name}" has been assigned to ${target.name}.`,
          );
          handleClosePreview();
          onWorkoutAssigned?.(res.data);
          onClose();
        },
        onError: error => {
          const message = error.response?.data?.message ?? 'Could not assign the workout. Please try again.';
          errorNotification('Assignment failed', message);
        },
      });
    },
    [
      selectedTemplate,
      templateData,
      date,
      target,
      assignMutation,
      trackConfirmed,
      successNotification,
      errorNotification,
      handleClosePreview,
      onClose,
      onWorkoutAssigned,
    ],
  );

  const handleEditBeforeAssign = useCallback((): void => {
    if (!selectedTemplate || !templateData) return;
    navigate('/workouts/programming/create', {
      state: {
        date,
        target,
        prefillData: {
          ...mapTemplateToFormValues(templateData),
          isVisibleToAthletes: defaultVisibleToAthletes,
        },
        replacesWorkoutId,
        seededFromTemplate: true,
        view,
      },
    });
    handleClosePreview();
    onClose();
  }, [
    selectedTemplate,
    templateData,
    navigate,
    date,
    target,
    view,
    handleClosePreview,
    onClose,
    defaultVisibleToAthletes,
    replacesWorkoutId,
  ]);

  const rowClassName = useCallback(
    (row: WorkoutTemplateList) => {
      return selectedTemplate?.workoutTemplateId === row.workoutTemplateId
        ? 'assign-modal-row-selected'
        : '';
    },
    [selectedTemplate],
  );

  const previewData = selectedTemplate && templateData ? buildPreviewDataFromTemplate(templateData) : null;

  return (
    <>
      <Stack spacing='md'>
        <TextInput
          placeholder='Search templates...'
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          icon={<IconSearch size={theme.fontSizes.sm} />}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: TABLE_HEIGHT,
            '& table tbody td': {
              paddingTop: `${theme.spacing.sm} !important`,
              paddingBottom: `${theme.spacing.sm} !important`,
            },
            '& table tbody tr': {
              cursor: 'pointer',
              transition: 'background-color 120ms ease',
              '&:hover': {
                backgroundColor: theme.colors.gray[0],
              },
            },
            '& .assign-modal-row-selected': {
              backgroundColor: theme.colors.blue[0],
              '&:hover': {
                backgroundColor: theme.colors.blue[1],
              },
            },
          }}
        >
          <Table
            fillHeight
            tableData={tableData}
            columns={columns}
            isSuccess={isSuccess}
            isLoading={isLoading}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            fetchNextPage={fetchNextPage ?? (() => {})}
            handleRowClick={handleRowClick}
            rowClassName={rowClassName}
            margin={0}
            bgc='white'
            EmptyState={
              <TemplatesEmptyState
                search={search}
                canCreate={canMutate}
                onCreateNew={() =>
                  navigate('/workouts/programming/create', { state: { date, target, view } })
                }
              />
            }
          />
        </Box>
      </Stack>

      <WorkoutPreviewModal
        opened={previewOpened}
        onClose={handleClosePreview}
        previewData={previewData}
        isLoading={templateLoading}
        onEditBeforeAssign={handleEditBeforeAssign}
        onAssign={handleAssign}
        defaultVisibleToAthletes={defaultVisibleToAthletes}
        assignLoading={assignMutation.isLoading}
      />
    </>
  );
};

export default PanelTemplate;
